import cn from 'classnames';
import Swiper, { Navigation } from 'swiper';
import { warehouseModel } from '@entities/warehouse';
import { formatBagId } from '@lib/bag-formatting';
import { dateFormatter, DATE_FORMAT } from '@lib/date-formatting';
import { $agricultureById } from '@@/agriculture/store/agriculture.store';
import { IGeometryJson } from 'yandex-maps';
import { renderToString } from 'react-dom/server';
import { BagMainParameters } from '@@/bags/components/bag-main-parameters';
import { DiContainer } from '@@/di/di-container';
import { WarehousesService } from '@@/warehouses/services/warehouses.service';
import { IWarehouse, WarehouseTypes } from '@@/warehouses/ts';
import { bagMapper } from '@@/bags/mappers/bag.mapper';
import { UiIcon } from '@@/ui/components/ui-icon';
import { WAREHOUSES_TYPES_ICON_DICTIONARY } from '@@/warehouses/dictionaries/types.dictionary';

import 'swiper/css';
import 'swiper/css/navigation';
import styles from './placemark.module.css';

const warehousesService = DiContainer.get(WarehousesService);

let mtx: { [id: number]: boolean } = {};

function initSwipeForStationary(warehouseInfo: IWarehouse) {
  function update(swiper: Swiper) {
    if (swiper.slides.length === 0) return;

    swiper.slides[swiper.activeIndex].style.zIndex = '1';
    swiper.slides.forEach((s, idx) => {
      const data = s.querySelector<HTMLElement>(`.${styles.bunkerData}`);

      if (data) {
        if (idx > swiper.activeIndex) {
          swiper.slides[idx].style.zIndex = `${-idx}`;
        }
        data.style.opacity = idx === swiper.activeIndex ? '1' : '0';
      }
    });
  }

  return new Swiper(`.${styles.swiper}`, {
    modules: [Navigation],
    direction: 'horizontal',
    loop: warehouseInfo.filledBags > 3,
    centeredSlides: true,
    navigation: {
      nextEl: `.swiper-button-next`,
      prevEl: '.swiper-button-prev',
    },
    slidesPerView: warehouseInfo.filledBags > 2 ? 2 : warehouseInfo.filledBags,
    slidesPerGroup: 1,
    on: {
      afterInit: update,
      slideChange: update,
    },
  });
}

function initSwipeForSleeves(warehouseInfo: IWarehouse) {
  const slidesCnt = warehouseInfo.filledBags > 4 ? 4 : warehouseInfo.filledBags;

  return new Swiper(`.${styles.swiper}`, {
    modules: [Navigation],
    direction: 'horizontal',
    loop: false,
    navigation: {
      nextEl: `.swiper-button-next`,
      prevEl: '.swiper-button-prev',
    },
    spaceBetween: 4,
    slidesPerGroup: slidesCnt,
    slidesPerView: slidesCnt,
  });
}

function initPlacemarkSwipe(warehouseInfo: IWarehouse) {
  switch (warehouseInfo.type) {
    case warehouseModel.WAREHOUSE_TYPES.sleeve:
      return initSwipeForSleeves(warehouseInfo);
    case warehouseModel.WAREHOUSE_TYPES.stationary:
      return initSwipeForStationary(warehouseInfo);
  }
}

const getBags = async (warehouse: IWarehouse, placemark: ymaps.Placemark) => {
  const { id, type } = warehouse;
  const agricultureById = $agricultureById.getState();
  const result = await warehousesService.findOneById(id);
  const bags = result.bags?.map(bagMapper) ?? [];

  let slides = (
    <>
      {bags
        .filter((bag) => bag.agricultureId)
        .map((bag) => {
          // Не делается проверка на отсутствие данных в стейте, т.к ожидается что данные уже будут загружены
          const currentCulture = agricultureById[bag.agricultureId as number];

          if (type === warehouseModel.WAREHOUSE_TYPES.sleeve) {
            // TODO: использовать filled-bag компонент
            return (
              <div className="swiper-slide">
                <div className={styles.container}>
                  <div
                    className={styles.item}
                    style={{
                      borderColor: currentCulture.color,
                      backgroundColor: currentCulture.color,
                    }}
                  >
                    <div className={styles.id}>{formatBagId(bag.id)}</div>
                    <div className={styles.title}>{currentCulture.name}</div>
                    <img
                      className={styles.img}
                      src={currentCulture.img}
                      alt={currentCulture.name}
                    />

                    <BagMainParameters
                      className={styles.params}
                      bag={bag}
                      mode="vertical"
                    />
                  </div>
                </div>
              </div>
            );
          }

          if (type === warehouseModel.WAREHOUSE_TYPES.stationary) {
            return (
              <div className="swiper-slide">
                <div className={cn(styles.container, styles.bunker)}>
                  <svg
                    width="400"
                    height="180"
                    viewBox="0 0 310 140"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.40459 126.736L1.40466 126.736C28.6432 71.2088 70.6129 28.2785 112.779 9.02848C137.696 -2.34285 166.312 -2.34281 191.221 9.02848C233.387 28.2785 275.349 71.2088 302.595 126.736L303.044 126.516L302.595 126.736C305.431 132.513 301.254 139.249 294.811 139.249H152.004H9.18919C2.74558 139.249 -1.4314 132.513 1.40459 126.736Z"
                      fill={currentCulture.color}
                      stroke="white"
                      strokeWidth="4"
                      transform="translate(3)"
                    />
                  </svg>

                  <div className={styles.bunkerData}>
                    <img
                      className={styles.bunkerData__img}
                      src={currentCulture.img}
                      alt={currentCulture.name}
                    />

                    <div className={styles.header}>
                      <span className={styles.bunkerId}>{formatBagId(bag.id)}</span>
                      <span className={styles.title}>{currentCulture.name}</span>
                      <span className={styles.filled}>
                        {dateFormatter(bag.fillingDate).format(DATE_FORMAT)}
                      </span>
                    </div>

                    <div className={styles.paramsBunker}>
                      <BagMainParameters bag={bag} />
                    </div>
                  </div>
                </div>
              </div>
            );
          }
        })}
    </>
  );

  return renderToString(slides);
};

export function createWhMarker(warehouseInfo: IWarehouse): ymaps.Placemark {
  const { id, name, type, marker: pos } = warehouseInfo;

  let placemark: ymaps.Placemark;
  const Icon = WAREHOUSES_TYPES_ICON_DICTIONARY[type as WarehouseTypes];

  // TODO: стилизовать через css
  const placemarkHeader = renderToString(
    <div
      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 8px' }}
    >
      <UiIcon style={{ width: '32px', height: '32px' }} icon={<Icon />} />
      <span>{name}</span>
    </div>,
  );
  const container = document.createElement('div');
  const marker = document.createElement('div');

  container.classList.add(styles.container);
  marker.classList.add(styles.placemark);
  marker.innerHTML = placemarkHeader;

  container.appendChild(marker);

  const c = document.body.appendChild(container);
  const xW = marker.clientWidth;
  c.remove();

  const hintClass =
    warehouseInfo.type === warehouseModel.WAREHOUSE_TYPES.sleeve
      ? styles.hintSleeve
      : styles.hintStation;

  const swiperClass =
    warehouseInfo.type === warehouseModel.WAREHOUSE_TYPES.sleeve
      ? styles.swiperSleeve
      : styles.swiperStation;

  const dataClass =
    warehouseInfo.type === warehouseModel.WAREHOUSE_TYPES.sleeve
      ? styles.dataSleeve
      : styles.dataStation;

  const maxC = warehouseInfo.filledBags > 4 ? 4 : warehouseInfo.filledBags;
  const w =
    warehouseInfo.type === warehouseModel.WAREHOUSE_TYPES.sleeve
      ? `width:calc(90px * ${maxC} + 90px)`
      : '';

  const hintLayout =
    warehouseInfo.filledBags > 0
      ? ymaps.templateLayoutFactory.createClass(
          `<div class='${styles.hint} ${hintClass}' style="${w}">
          ${placemarkHeader} 
          <div class='${styles.data} ${dataClass}'>
            [if properties.isLoaded]
              <!-- Slider main container -->
              <div class="${styles.swiper} ${swiperClass}">
                <!-- Additional required wrapper -->
                <div class="swiper-wrapper">
                  <!-- Slides -->
                  $[properties.slides]
                </div>
                <div class="swiper-button-prev ${styles.prev}"><button></button></div>
                <div class="swiper-button-next ${styles.next}"><button></button></div>
              </div>
            [else]
              <div class='${styles.loader}'></div>
            [endif]
          </div>
        </div>`,
          {
            build: function () {
              this.constructor.superclass.build.call(this);
              if (mtx[id]) return;

              const to = placemark.properties.get('timeout', '01-01-1970');

              if (dateFormatter(new Date()).diff(to) > 5000) {
                placemark.properties.set('isLoaded', '');
              }

              const isLoaded = placemark.properties.get('isLoaded', '');

              if (!isLoaded) {
                mtx[id] = true;
                getBags(warehouseInfo, placemark)
                  .then((s) => {
                    placemark.properties.set('slides', s);
                    placemark.properties.set('isLoaded', 'OK');
                    placemark.properties.set(
                      'timeout',
                      dateFormatter(new Date()).toISOString(),
                    );
                    initPlacemarkSwipe(warehouseInfo);
                  })
                  .finally(() => delete mtx[id]);
              } else {
                initPlacemarkSwipe(warehouseInfo);
              }
            },

            getShape: function () {
              const el = this.getElement();
              let result = null;

              if (el) {
                const firstChild = el.firstChild;

                result = new ymaps.shape.Rectangle(
                  new ymaps.geometry.pixel.Rectangle([
                    [0, 0],
                    [firstChild.offsetWidth + 730, firstChild.offsetHeight + 20],
                  ]),
                );
              }

              return result;
            },
          },
        )
      : null;

  placemark = new ymaps.Placemark(
    pos,
    {},
    {
      iconLayout: ymaps.templateLayoutFactory.createClass(marker.outerHTML),
      hintLayout,
      iconShape: {
        type: 'Rectangle',
        coordinates: [
          [-25, -25],
          [xW - 25, 25],
        ],
      } as IGeometryJson,
    },
  );

  return placemark;
}
